import { Add, CalendarMonth, EventAvailable } from '@mui/icons-material'
import { translate } from 'locales'
import { formatLocalCap } from 'utils/date'
import React from 'react'
import { useTheme } from 'styled-components'
import { ITheme } from 'theme/interface'
import { IFinancialMonthDetail, MonthlyProductionStatus } from '../types'
import { formatCurrency, formatDate } from '../utils'
import * as S from './styles'
import { statusColor, statusLabelKey } from './statusColors'

// Previsão de pagamento oculta até as novas regras de negócio
const SHOW_PAYMENT_FORECAST = false

interface MonthSummaryCardsProps {
  detail: IFinancialMonthDetail
  status: MonthlyProductionStatus
  locale: string
  isPsychology: boolean
}

const MonthSummaryCards = ({
  detail,
  status,
  locale,
  isPsychology,
}: MonthSummaryCardsProps) => {
  const theme = useTheme() as ITheme
  const monthLabel = formatLocalCap(`${detail.periodMonth}-01`, 'MMMM')
  const totalConsultations =
    detail.stats.finished30 +
    detail.stats.finished60 +
    detail.stats.noShow30 +
    detail.stats.noShow60 +
    detail.stats.canceledUnder30_30 +
    detail.stats.canceledUnder30_60

  return (
    <S.Grid>
      <S.ProductionCard>
        <S.CardTitle>
          <Add />
          {translate('financial.overview.monthProduction')} {monthLabel}
        </S.CardTitle>

        <S.CardValue>
          {formatCurrency(detail.totalValue, locale)}
        </S.CardValue>
      </S.ProductionCard>

      <S.Card>
        <S.CardTitle>
          <S.IconCircle>
            <EventAvailable />
          </S.IconCircle>
          {translate('financial.overview.attendedConsultations')}
        </S.CardTitle>

        <S.ConsultationsCount>{totalConsultations}</S.ConsultationsCount>

        <S.ConsultationsList>
          {isPsychology ? (
            <>
              <li>
                <S.Dot color={theme.colors.success} />
                {detail.stats.finished30}{' '}
                {translate('financial.overview.finished30')}
              </li>
              <li>
                <S.Dot color={theme.colors.success} />
                {detail.stats.finished60}{' '}
                {translate('financial.overview.finished60')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {detail.stats.noShow30} {translate('financial.overview.noShow30')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {detail.stats.noShow60} {translate('financial.overview.noShow60')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {detail.stats.canceledUnder30_30}{' '}
                {translate('financial.overview.canceled30')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {detail.stats.canceledUnder30_60}{' '}
                {translate('financial.overview.canceled60')}
              </li>
            </>
          ) : (
            <>
              <li>
                <S.Dot color={theme.colors.success} />
                {detail.stats.finished30 + detail.stats.finished60}{' '}
                {translate('financial.overview.finished')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {detail.stats.noShow30 + detail.stats.noShow60}{' '}
                {translate('financial.overview.noShow')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {detail.stats.canceledUnder30_30 + detail.stats.canceledUnder30_60}{' '}
                {translate('financial.overview.canceled')}
              </li>
            </>
          )}
        </S.ConsultationsList>
      </S.Card>

      <S.Card>
        <S.CardTitle>
          <S.IconCircle>
            <CalendarMonth />
          </S.IconCircle>
          {translate('financial.overview.paymentStatus')}
        </S.CardTitle>

        <S.PaymentStatus>
          <div className="status-row" style={{ color: statusColor(status) }}>
            <S.Dot color={statusColor(status)} />
            {translate(statusLabelKey(status))}
          </div>

          <div className="status-detail">
            <span className="label">{translate('financial.closingDate')}</span>
            <span className="value">{formatDate(detail.closingDate)}</span>
          </div>

          {SHOW_PAYMENT_FORECAST && (
            <div className="status-detail">
              <span className="label">
                {translate('financial.overview.expected')}
              </span>
              <span className="value">
                {detail.paymentMonth
                  ? formatLocalCap(`${detail.paymentMonth}-01`, 'MMM yyyy')
                  : translate('financial.notAvailable')}
              </span>
            </div>
          )}
        </S.PaymentStatus>
      </S.Card>
    </S.Grid>
  )
}

export default MonthSummaryCards
